/**
 * Client-side sanitiser and plain-text renderer for the compose editor.
 *
 * Input is whatever the contentEditable produced (including pasted or programmatically inserted
 * markup, which is untrusted). The HTML is parsed with DOMParser (an inert document: no scripts run,
 * no resources load) and re-serialised from an allow-list: only known tags survive, the only
 * attribute kept is `href` on <a> and only for http(s)/mailto. The server sanitises again; this keeps
 * hostile markup from ever being sent.
 */

const ALLOWED_TAGS = new Set([
  'a', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'p', 'br', 'div', 'span',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'h1', 'h2', 'h3',
]);
const CANONICAL_TAG: Record<string, string> = { strike: 's' };
/** Dropped together with their content (never unwrapped). */
const DROP_WITH_CONTENT = new Set([
  'script', 'style', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'noscript',
  'template', 'svg', 'math', 'link', 'meta', 'base', 'form', 'input', 'button', 'textarea',
  'select', 'head', 'title', 'audio', 'video', 'canvas', 'img', 'picture',
]);
const VOID_TAGS = new Set(['br']);

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

const escapeText = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeAttr = (s: string) => escapeText(s).replace(/"/g, '&quot;');

/** Returns a normalised http(s)/mailto URL or null. Strips control characters used to smuggle schemes. */
export function safeHref(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // eslint-disable-next-line no-control-regex
  const cleaned = raw.replace(/[\u0000- \u007f-\u009f]/g, '');
  return SAFE_HREF.test(cleaned) ? cleaned : null;
}

function serialize(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return escapeText(node.textContent ?? '');
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (DROP_WITH_CONTENT.has(tag)) return '';
  const inner = Array.from(el.childNodes).map(serialize).join('');
  if (!ALLOWED_TAGS.has(tag)) return inner; // unknown wrapper: keep its (sanitised) children
  const out = CANONICAL_TAG[tag] ?? tag;
  if (VOID_TAGS.has(out)) return `<${out}>`;
  if (out === 'a') {
    const href = safeHref(el.getAttribute('href'));
    return href ? `<a href="${escapeAttr(href)}" rel="noopener noreferrer">${inner}</a>` : inner;
  }
  return `<${out}>${inner}</${out}>`;
}

export function sanitizeEditorHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  return Array.from(doc.body.childNodes).map(serialize).join('');
}

const BLOCKS = new Set(['div', 'p', 'li', 'ul', 'ol', 'blockquote', 'pre', 'h1', 'h2', 'h3']);

/** Plain-text rendering of (sanitised) editor HTML: blocks become lines, lists get bullets, links keep their URL. */
export function htmlToPlainText(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  let out = '';
  const atLineStart = () => out === '' || out.endsWith('\n');
  const ensureLineStart = () => {
    if (!atLineStart()) out += '\n';
  };

  const walk = (node: Node, listCtx: { ordered: boolean; n: number } | null, inPre: boolean) => {
    if (node.nodeType === Node.TEXT_NODE) {
      let text = (node.textContent ?? '').replace(/\u00a0/g, ' ');
      if (!inPre) text = text.replace(/[ \t\r\n]+/g, ' ');
      if (atLineStart()) text = text.replace(/^ +/, '');
      out += text;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (tag === 'br') {
      out += '\n';
      return;
    }
    if (tag === 'a') {
      const href = el.getAttribute('href') ?? '';
      const start = out.length;
      el.childNodes.forEach((c) => walk(c, listCtx, inPre));
      const label = out.slice(start).trim();
      if (href && label !== href && label !== href.replace(/^mailto:/i, '')) out += ` (${href})`;
      return;
    }
    if (tag === 'ul' || tag === 'ol') {
      ensureLineStart();
      const ctx = { ordered: tag === 'ol', n: 0 };
      el.childNodes.forEach((c) => walk(c, ctx, inPre));
      ensureLineStart();
      return;
    }
    if (tag === 'li') {
      ensureLineStart();
      if (listCtx) {
        listCtx.n += 1;
        out += listCtx.ordered ? `${listCtx.n}. ` : '- ';
      }
      el.childNodes.forEach((c) => walk(c, null, inPre));
      ensureLineStart();
      return;
    }
    if (BLOCKS.has(tag)) {
      ensureLineStart();
      el.childNodes.forEach((c) => walk(c, listCtx, inPre || tag === 'pre'));
      ensureLineStart();
      return;
    }
    el.childNodes.forEach((c) => walk(c, listCtx, inPre));
  };

  doc.body.childNodes.forEach((c) => walk(c, null, false));
  return out.replace(/[ \t]+\n/g, '\n').replace(/\s+$/, '');
}

/** Initial editor markup for prefilled plain text (reply quote, forward header): one <div> per line, text escaped. */
export function plainTextToEditorHtml(text: string): string {
  if (!text) return '';
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => (line === '' ? '<div><br></div>' : `<div>${escapeText(line)}</div>`))
    .join('');
}
